/**
 * Admin → Lead Magnets panel.
 *
 * Lists every email captured via /free-templates with download status,
 * drip-email progress, conversion status, and CSV export.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, Download, RefreshCw, Loader2, CheckCircle2, Copy, Check, ExternalLink, FileText, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

const StatCard = ({ label, value, accent = 'blue' }) => {
  const tones = {
    blue:    'from-blue-50 to-cyan-50 border-blue-200 text-blue-900',
    emerald: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900',
    amber:   'from-amber-50 to-orange-50 border-amber-200 text-amber-900',
    purple:  'from-purple-50 to-indigo-50 border-purple-200 text-purple-900',
    rose:    'from-rose-50 to-pink-50 border-rose-200 text-rose-900',
  };
  return (
    <div className={`bg-gradient-to-br ${tones[accent]} border-2 rounded-xl p-4`}>
      <p className="text-[11px] font-bold uppercase tracking-wider opacity-70">{label}</p>
      <p className="text-3xl font-extrabold mt-1 leading-none">{value ?? '—'}</p>
    </div>
  );
};

// Small "copy to clipboard" pill — used inside the Share & Promote card.
function CopyField({ label, value, hint, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — user can still select manually */ }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{label}</p>
        <button
          onClick={onCopy}
          className={`text-[11px] font-bold px-2 py-1 rounded-md flex items-center gap-1 ${copied ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
          data-testid={`${testid}-copy`}
        >
          {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <code className="block bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-[12px] text-slate-900 font-mono break-all">{value}</code>
      {hint && <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{hint}</p>}
    </div>
  );
}

function SharePromoteCard({ origin }) {
  const captureUrl   = `${origin}/free-templates`;
  const pdfUrl       = `${origin}/api/lead-magnet/pdf`;
  const adHeadline   = "Free: 12 Dental Templates Every Practice Needs";
  const adPrimary    = "Real post-op instructions, informed consents, medication cards, and emergency guides — formatted exactly the way they print from PractCom. Built by a dentist with 30+ years in practice. Download free, no credit card.";
  const adCta        = "Get the Free Templates";
  const emailSubject = "Free: 12 dental templates every practice needs";
  const emailBody    = `Hey,\n\nI put together a free PDF with 12 of the most-used patient communication templates from PractCom — post-op instructions, informed consents, medication cards, and emergency guides.\n\nGrab it here (no credit card needed):\n${captureUrl}\n\n— Cary Ganz DDS\nFounder, PractCom`;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 space-y-4" data-testid="leadmagnet-share-card">
      <div className="flex items-start gap-3">
        <div className="bg-blue-600 text-white rounded-lg p-2.5"><Share2 className="w-5 h-5" /></div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-blue-900">Share &amp; Promote</h3>
          <p className="text-xs text-blue-800 mt-0.5 max-w-2xl">
            URLs and ad copy for Facebook, Instagram, LinkedIn, and email. Always use the <b>Capture URL</b> in ads — it collects the email before delivering the PDF.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CopyField
          label="Capture URL · Use in Facebook ads"
          value={captureUrl}
          testid="leadmagnet-url-capture"
          hint="Email-gated landing page. Visitors enter their info, get the PDF by email, and enter your Day-3 / Day-7 drip."
        />
        <CopyField
          label="Direct PDF URL · Internal use only"
          value={pdfUrl}
          testid="leadmagnet-url-pdf"
          hint="⚠️ Bypasses lead capture. Don't post this on social — share the Capture URL instead."
        />
      </div>

      <div className="bg-white border border-blue-200 rounded-lg p-4 space-y-3" data-testid="leadmagnet-ad-copy">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Facebook / Instagram Ad Copy
          </p>
        </div>
        <div className="space-y-2 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Headline (40 char limit)</p>
            <p className="text-slate-900 font-bold">{adHeadline}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Primary text</p>
            <p className="text-slate-800 leading-snug">{adPrimary}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <p className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Call to action</p>
              <p className="text-slate-900 font-bold">{adCta}</p>
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase text-slate-500 mb-0.5">Destination URL</p>
              <code className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 break-all">{captureUrl}</code>
            </div>
          </div>
        </div>
        <button
          onClick={async () => {
            const full = `Headline: ${adHeadline}\n\nPrimary text:\n${adPrimary}\n\nCall to action: ${adCta}\nDestination: ${captureUrl}`;
            try { await navigator.clipboard.writeText(full); } catch { /* ignore */ }
          }}
          className="text-[11px] font-bold bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1.5 rounded-md flex items-center gap-1"
          data-testid="leadmagnet-ad-copy-btn"
        >
          <Copy className="w-3 h-3" /> Copy all ad copy
        </button>
      </div>

      <div className="bg-white border border-blue-200 rounded-lg p-4 space-y-2" data-testid="leadmagnet-email-template">
        <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Outreach Email Template</p>
        <div className="text-xs text-slate-700 space-y-1">
          <p><b>Subject:</b> {emailSubject}</p>
          <pre className="whitespace-pre-wrap font-sans bg-slate-50 border border-slate-200 rounded p-2 text-[12px]">{emailBody}</pre>
        </div>
        <button
          onClick={async () => {
            try { await navigator.clipboard.writeText(`Subject: ${emailSubject}\n\n${emailBody}`); } catch { /* ignore */ }
          }}
          className="text-[11px] font-bold bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1.5 rounded-md flex items-center gap-1"
          data-testid="leadmagnet-email-copy-btn"
        >
          <Copy className="w-3 h-3" /> Copy email
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <a
          href={captureUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] font-bold bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg"
          data-testid="leadmagnet-open-capture"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open capture page
        </a>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-[12px] font-bold bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-2 rounded-lg"
          data-testid="leadmagnet-preview-pdf"
        >
          <FileText className="w-3.5 h-3.5" /> Preview the PDF
        </a>
      </div>
    </div>
  );
}

const NewsletterLeads = ({ adminToken }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/admin/leads/newsletter`, { headers: { Authorization: `Bearer ${adminToken}` }, cache: 'no-store' })
      .then((r) => r.json()).then(setD).catch(() => setD({ leads: [] }));
  }, [adminToken]);
  if (!d) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-newsletter-leads">
      <h3 className="text-lg font-extrabold text-slate-900">Sample Newsletter Leads</h3>
      <p className="text-sm text-slate-600 mb-3">Dentists who downloaded the sample patient newsletter from the landing page · {d.total || 0} total · {d.downloaded || 0} downloaded</p>
      {(d.leads || []).length === 0 ? (
        <div className="text-sm text-slate-500">No newsletter leads yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><th className="px-3 py-2 text-left">Email / Name</th><th className="px-3 py-2 text-left">Source</th><th className="px-3 py-2 text-left">Requested</th><th className="px-3 py-2 text-left">Downloads</th></tr>
            </thead>
            <tbody>
              {d.leads.map((r) => (
                <tr key={r.email} className="border-b border-slate-100" data-testid="admin-newsletter-lead-row">
                  <td className="px-3 py-2"><div className="font-semibold">{r.email}</div><div className="text-xs text-slate-500">{r.name}</div></td>
                  <td className="px-3 py-2 text-slate-600">{r.source || '—'}</td>
                  <td className="px-3 py-2 text-slate-600">{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                  <td className="px-3 py-2">{r.downloadCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function LeadMagnetAdminPanel({ adminToken }) {
  const [data, setData] = useState({ counts: {}, leads: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(`${API}/api/admin/leads/templates`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        cache: 'no-store',
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { detail = (await res.json()).detail || detail; } catch {}
        throw new Error(detail);
      }
      setData(await res.json());
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = data.leads || [];
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || '').toLowerCase().includes(q) ||
      (r.name || '').toLowerCase().includes(q) ||
      (r.practiceName || '').toLowerCase().includes(q)
    );
  }, [data, filter]);

  const exportCsv = () => {
    const rows = data.leads || [];
    if (!rows.length) return;
    const headers = ['email', 'name', 'practiceName', 'source', 'createdAt', 'downloadedAt', 'downloadCount', 'dripDay3SentAt', 'dripDay7SentAt', 'convertedToTrialAt'];
    const csv = [
      headers.join(','),
      ...rows.map((r) =>
        headers.map((h) => {
          const v = r[h];
          if (v == null) return '';
          const s = String(v).replace(/"/g, '""');
          return s.includes(',') || s.includes('"') ? `"${s}"` : s;
        }).join(',')
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `practcom-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const c = data.counts || {};
  // Origin = whatever domain the admin is currently using (app.practcom.com
  // in prod, the preview URL in preview). Keeps URLs always-correct.
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.practcom.com';

  return (
    <div data-testid="admin-leadmagnet-panel" className="space-y-4">
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="bg-emerald-600 text-white rounded-lg p-3"><BookOpen className="w-6 h-6" /></div>
          <div>
            <h2 className="text-xl font-extrabold text-emerald-900">Lead Magnets — Free Template PDF</h2>
            <p className="text-sm text-emerald-800 mt-1 max-w-2xl">
              Every email captured at <code className="bg-white px-1.5 py-0.5 rounded text-[11px] border border-emerald-200">/free-templates</code>.
              Day-3 and Day-7 drip emails fire automatically. Once a lead signs up for a PractCom trial, they're auto-marked converted and dropped from this drip.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm font-bold">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            data-testid="leadmagnet-export"
            onClick={exportCsv}
            disabled={!visible.length}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg flex items-center gap-1.5 text-sm font-bold"
          >
            <Download className="w-4 h-4" /> CSV ({visible.length})
          </button>
        </div>
      </div>

      <SharePromoteCard origin={origin} />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Total Leads" value={c.total} accent="blue" />
        <StatCard label="Downloaded" value={c.downloaded} accent="emerald" />
        <StatCard label="Day 3 Sent" value={c.drip_d3_sent} accent="amber" />
        <StatCard label="Day 7 Sent" value={c.drip_d7_sent} accent="purple" />
        <StatCard label="Converted to Trial" value={c.converted} accent="rose" />
      </div>

      <input
        type="text"
        data-testid="leadmagnet-search"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by email, name, or practice…"
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />

      {err && (
        <div className="bg-rose-50 border border-rose-300 rounded-xl p-3 text-sm text-rose-800">{err}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No leads yet. Share <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">/free-templates</code> on Facebook to start capturing.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Email / Name</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Practice</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Signed Up</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Downloaded</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Day 3 / 7</th>
                  <th className="px-3 py-2 text-left font-bold text-slate-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} data-testid={`leadmagnet-row-${r.id}`} className="border-b border-slate-100 hover:bg-emerald-50/30">
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900">{r.email}</div>
                      <div className="text-xs text-slate-500">{r.name || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.practiceName || '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{fmt(r.createdAt)}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {r.downloadedAt
                        ? <span className="text-emerald-700">{fmt(r.downloadedAt)} · {r.downloadCount || 1}×</span>
                        : <span className="text-slate-400">never</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs space-x-1">
                        <span className={r.dripDay3SentAt ? 'text-amber-700 font-bold' : 'text-slate-400'}>D3</span>
                        <span className="text-slate-300">·</span>
                        <span className={r.dripDay7SentAt ? 'text-purple-700 font-bold' : 'text-slate-400'}>D7</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {r.convertedToTrialAt ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                          <CheckCircle2 className="w-3 h-3" /> Converted
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold">
                          Lead
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <NewsletterLeads adminToken={adminToken} />
    </div>
  );
}
